import React, { useEffect, useState } from "react"
import "../styles/service-notification.scss"

const STORAGE_KEY = "svcNotifDismissed"
const FIRST_DELAY_MS = 7000
const REOPEN_AFTER_MS = 90000

const playPing = () => {
  if (typeof window === "undefined") return
  try {
    const Ctx =
      (window as any).AudioContext || (window as any).webkitAudioContext
    if (!Ctx) return
    const ctx: AudioContext = new Ctx()
    if (ctx.state === "suspended") ctx.resume().catch(() => {})

    const master = ctx.createGain()
    master.gain.value = 0.08
    master.connect(ctx.destination)

    const tone = (freq: number, start: number, dur: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = "sine"
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0, ctx.currentTime + start)
      gain.gain.linearRampToValueAtTime(1, ctx.currentTime + start + 0.012)
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        ctx.currentTime + start + dur
      )
      osc.connect(gain)
      gain.connect(master)
      osc.start(ctx.currentTime + start)
      osc.stop(ctx.currentTime + start + dur + 0.02)
    }

    tone(880, 0, 0.18)
    tone(1318.5, 0.11, 0.22)

    window.setTimeout(() => ctx.close().catch(() => {}), 700)
  } catch {
    /* audio blocked or unsupported — silent fail */
  }
}

const ServiceNotification: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    if (typeof window === "undefined") return
    const dismissedAt = Number(sessionStorage.getItem(STORAGE_KEY) || 0)
    const elapsed = Date.now() - dismissedAt
    const wait =
      dismissedAt && elapsed < REOPEN_AFTER_MS
        ? REOPEN_AFTER_MS - elapsed
        : FIRST_DELAY_MS
    const t = window.setTimeout(() => {
      setOpen(true)
      playPing()
    }, wait)
    return () => window.clearTimeout(t)
  }, [])

  const dismiss = () => {
    setOpen(false)
    if (typeof window !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, String(Date.now()))
    }
  }

  if (!mounted) return null

  return (
    <div
      className={`svc-notif ${open ? "svc-notif--open" : ""}`}
      role="dialog"
      aria-live="polite"
      aria-hidden={!open}
    >
      <div className="svc-notif__frame">
        <div className="svc-notif__bar">
          <span className="svc-notif__dot" />
          <span className="svc-notif__title">incoming :: ayemun.hossain</span>
          <button
            type="button"
            className="svc-notif__close"
            onClick={dismiss}
            aria-label="dismiss"
          >
            ×
          </button>
        </div>

        <div className="svc-notif__body">
          <p className="svc-notif__line">
            <span className="svc-notif__prompt">$</span>
            <span className="svc-notif__cmd">whois --availability</span>
          </p>
          <p className="svc-notif__msg">
            Need an <em>AI Engineer</em>, <em>Backend</em>, or{" "}
            <em>DevSecOps</em> hand?
          </p>
          <p className="svc-notif__sub">
            Building RAG pipelines, scalable APIs, hardened cloud infra — open
            for projects &amp; collaborations.
          </p>

          <div className="svc-notif__links">
            <a
              href="mailto:ayemunh@gmail.com"
              className="svc-notif__chip"
              onClick={dismiss}
            >
              <span className="svc-notif__chip-k">mail</span>
              <span className="svc-notif__chip-v">ayemunh@gmail.com</span>
            </a>
            <a
              href="https://www.linkedin.com/in/ayemunh/"
              target="_blank"
              rel="noopener noreferrer"
              className="svc-notif__chip"
              onClick={dismiss}
            >
              <span className="svc-notif__chip-k">linkedin</span>
              <span className="svc-notif__chip-v">/in/ayemunh</span>
            </a>
            <a
              href="https://www.github.com/AyemunHossain"
              target="_blank"
              rel="noopener noreferrer"
              className="svc-notif__chip"
              onClick={dismiss}
            >
              <span className="svc-notif__chip-k">github</span>
              <span className="svc-notif__chip-v">/AyemunHossain</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(ServiceNotification)
